import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  getFundRequests, 
  createFundRequest, 
  reviewFundRequest, 
  cancelFundRequest,
  getPendingFundRequestsCount,
  reverseFundRequest
} from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Warning,
  CreditCard,
  AmazonLogo,
  Trash,
  ArrowCounterClockwise
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function FundRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reverseReason, setReverseReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingInfo, setPendingInfo] = useState({ pending_count: 0, recent_rejections: [] });
  const [formData, setFormData] = useState({
    amount: '',
    source: 'card',
    notes: ''
  });

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdminOrAbove = isOwner || user?.role === 'assistant';
  const isAdmin = isAdminOrAbove;
  const isStaff = user?.role === 'staff';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const [requestsRes, pendingRes] = await Promise.all([
        getFundRequests(status),
        getPendingFundRequestsCount()
      ]);
      setRequests(requestsRes.data);
      setPendingInfo(pendingRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await createFundRequest({
        amount: parseFloat(formData.amount),
        source: formData.source,
        notes: formData.notes || null
      });
      toast.success('Fund request submitted for approval');
      setDialogOpen(false);
      setFormData({ amount: '', source: 'card', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await reviewFundRequest(requestId, { action: 'approve' });
      toast.success('Fund request approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    try {
      await reviewFundRequest(selectedRequest.id, { 
        action: 'reject',
        rejection_reason: rejectionReason || 'Request rejected by admin'
      });
      toast.success('Fund request rejected');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    try {
      await cancelFundRequest(requestId);
      toast.success('Request cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const openReverseDialog = (request) => {
    setSelectedRequest(request);
    setReverseReason('');
    setReverseDialogOpen(true);
  };

  const handleReverseFund = async () => {
    if (!selectedRequest) return;
    if (!reverseReason.trim()) {
      toast.error('Please provide a reason for reversal');
      return;
    }
    try {
      await reverseFundRequest(selectedRequest.id, { reason: reverseReason });
      toast.success('Fund request reversed successfully');
      setReverseDialogOpen(false);
      setSelectedRequest(null);
      setReverseReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadge = (status, isReversed) => {
    if (isReversed) {
      return <span className="flex items-center gap-1 text-purple-600"><ArrowCounterClockwise size={16} /> Reversed</span>;
    }
    switch (status) {
      case 'pending':
        return <span className="flex items-center gap-1 text-orange-600"><Clock size={16} /> Pending</span>;
      case 'approved':
        return <span className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /> Approved</span>;
      case 'rejected':
        return <span className="flex items-center gap-1 text-red-600"><XCircle size={16} /> Rejected</span>;
      default:
        return status;
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'card':
        return <span className="flex items-center gap-1"><CreditCard size={16} className="text-blue-600" /> Card</span>;
      case 'amazon':
        return <span className="flex items-center gap-1"><AmazonLogo size={16} className="text-[#FF9900]" /> Amazon</span>;
      case 'tataneu':
        return <span className="badge-tataneu">TataNeu</span>;
      default:
        return source;
    }
  };

  // Filter requests by tab
  const pendingRequests = requests.filter(r => r.status === 'pending');
  const approvedRequests = requests.filter(r => r.status === 'approved');
  const rejectedRequests = requests.filter(r => r.status === 'rejected');

  return (
    <div className="space-y-6" data-testid="fund-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Card / Gift Card Fund Request</h1>
        <div className="flex items-center gap-4">
          {/* Status Filter for Admin */}
          {isAdmin && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          )}
          
          {/* Staff can create fund requests */}
          {isStaff && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-fund-request-btn">
                  <Plus size={18} className="mr-2" /> Request Fund
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Fund (Pending Approval)</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="form-label">Amount (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="form-input"
                      placeholder="Enter amount received"
                      required
                      data-testid="fund-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Source</Label>
                    <SearchableSelect
                      options={[
                        { value: 'card', label: 'Card' },
                        { value: 'amazon', label: 'Amazon' },
                        { value: 'tataneu', label: 'TataNeu' }
                      ]}
                      value={formData.source}
                      onValueChange={(v) => setFormData({ ...formData, source: v })}
                      placeholder="Select Source"
                      data-testid="fund-source-select"
                    />
                  </div>
                  <div>
                    <Label className="form-label">Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="form-input"
                      placeholder="Any additional notes"
                      data-testid="fund-notes-input"
                    />
                  </div>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
                    <strong>Note:</strong> Your fund request will be sent to Admin for approval. 
                    Balance will only be added after approval.
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="fund-submit-btn">
                    Submit Request
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Staff Notifications */}
      {isStaff && pendingInfo.pending_count > 0 && (
        <div className="p-4 bg-orange-50 border border-orange-200 flex items-start gap-3" data-testid="pending-alert">
          <Clock size={24} className="text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-orange-800">Fund Requests Pending</h3>
            <p className="text-sm text-orange-700">
              You have {pendingInfo.pending_count} fund request(s) waiting for admin approval.
            </p>
          </div>
        </div>
      )}

      {isStaff && pendingInfo.recent_rejections?.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 flex items-start gap-3" data-testid="rejection-alert">
          <XCircle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Fund Request Rejected</h3>
            {pendingInfo.recent_rejections.map(r => (
              <p key={r.id} className="text-sm text-red-700">
                {formatCurrency(r.amount)} ({r.source}) - Reason: {r.rejection_reason}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Admin Pending Count */}
      {isAdmin && pendingInfo.pending_count > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 flex items-start gap-3" data-testid="admin-pending-alert">
          <Warning size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800">Pending Approvals</h3>
            <p className="text-sm text-blue-700">
              {pendingInfo.pending_count} fund request(s) waiting for your approval.
            </p>
          </div>
        </div>
      )}

      <Tabs defaultValue="pending" className="w-full">
        <TabsList>
          <TabsTrigger value="pending">
            Pending {pendingRequests.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded-full">{pendingRequests.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4">
          <div className="card-flat overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>TXN ID</TableHead>
                    {isAdmin && <TableHead>Staff</TableHead>}
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Requested At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-gray-500">
                        No pending requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingRequests.map((r) => (
                      <TableRow key={r.id} data-testid={`pending-row-${r.id}`}>
                        <TableCell className="font-mono text-xs text-gray-600">{r.transaction_id || '-'}</TableCell>
                        {isAdmin && <TableCell className="font-medium">{r.staff_name}</TableCell>}
                        <TableCell className="numeric font-semibold text-green-600">+{formatCurrency(r.amount)}</TableCell>
                        <TableCell>{getSourceBadge(r.source)}</TableCell>
                        <TableCell className="text-gray-500 max-w-[200px] truncate">{r.notes || '-'}</TableCell>
                        <TableCell>{formatDateTime(r.created_at)}</TableCell>
                        <TableCell className="text-right">
                          {isAdmin ? (
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApprove(r.id)}
                                data-testid={`approve-${r.id}`}
                              >
                                <CheckCircle size={16} className="mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openRejectDialog(r)}
                                data-testid={`reject-${r.id}`}
                              >
                                <XCircle size={16} className="mr-1" /> Reject
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => handleCancel(r.id)}
                              data-testid={`cancel-${r.id}`}
                            >
                              <Trash size={16} /> Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>TXN ID</TableHead>
                  {isAdmin && <TableHead>Staff</TableHead>}
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Approved By</TableHead>
                  <TableHead>Approved At</TableHead>
                  {isOwner && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {approvedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isOwner ? 9 : isAdmin ? 8 : 7} className="text-center text-gray-500">
                      No approved requests
                    </TableCell>
                  </TableRow>
                ) : (
                  approvedRequests.map((r) => (
                    <TableRow key={r.id} className={r.is_reversed ? 'bg-purple-50' : ''} data-testid={`approved-row-${r.id}`}>
                      <TableCell className="font-mono text-xs text-gray-600">{r.transaction_id || '-'}</TableCell>
                      {isAdmin && <TableCell className="font-medium">{r.staff_name}</TableCell>}
                      <TableCell className={`numeric font-semibold ${r.is_reversed ? 'text-purple-600 line-through' : 'text-green-600'}`}>
                        {r.is_reversed ? '' : '+'}{formatCurrency(r.amount)}
                      </TableCell>
                      <TableCell>{getSourceBadge(r.source)}</TableCell>
                      <TableCell>{getStatusBadge(r.status, r.is_reversed)}</TableCell>
                      <TableCell className="text-gray-500 max-w-[150px] truncate">{r.notes || '-'}</TableCell>
                      <TableCell>{r.reviewed_by || '-'}</TableCell>
                      <TableCell>{r.reviewed_at ? formatDateTime(r.reviewed_at) : '-'}</TableCell>
                      {isOwner && (
                        <TableCell className="text-right">
                          {!r.is_reversed && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              onClick={() => openReverseDialog(r)}
                              data-testid={`reverse-fund-${r.id}`}
                            >
                              <ArrowCounterClockwise size={16} className="mr-1" /> Reverse
                            </Button>
                          )}
                          {r.is_reversed && (
                            <span className="text-xs text-purple-500">Reversed</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>Staff</TableHead>}
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Requested At</TableHead>
                  <TableHead>Rejection Reason</TableHead>
                  <TableHead>Rejected At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rejectedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-gray-500">
                      No rejected requests
                    </TableCell>
                  </TableRow>
                ) : (
                  rejectedRequests.map((r) => (
                    <TableRow key={r.id} className="bg-red-50" data-testid={`rejected-row-${r.id}`}>
                      {isAdmin && <TableCell className="font-medium">{r.staff_name}</TableCell>}
                      <TableCell className="numeric font-semibold text-red-600">{formatCurrency(r.amount)}</TableCell>
                      <TableCell>{getSourceBadge(r.source)}</TableCell>
                      <TableCell className="text-gray-500 max-w-[200px] truncate">{r.notes || '-'}</TableCell>
                      <TableCell>{formatDateTime(r.created_at)}</TableCell>
                      <TableCell className="text-red-600">{r.rejection_reason || '-'}</TableCell>
                      <TableCell>{r.reviewed_at ? formatDateTime(r.reviewed_at) : '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* All Tab */}
        <TabsContent value="all" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>Staff</TableHead>}
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Requested At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-gray-500">
                      No fund requests
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((r) => (
                    <TableRow key={r.id} className={r.status === 'rejected' ? 'bg-red-50' : ''}>
                      {isAdmin && <TableCell className="font-medium">{r.staff_name}</TableCell>}
                      <TableCell className={`numeric font-semibold ${r.status === 'approved' ? 'text-green-600' : r.status === 'rejected' ? 'text-red-600' : ''}`}>
                        {r.status === 'approved' ? '+' : ''}{formatCurrency(r.amount)}
                      </TableCell>
                      <TableCell>{getSourceBadge(r.source)}</TableCell>
                      <TableCell>{getStatusBadge(r.status)}</TableCell>
                      <TableCell className="text-gray-500 max-w-[150px] truncate">{r.notes || '-'}</TableCell>
                      <TableCell>{formatDateTime(r.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Fund Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject this fund request of {selectedRequest && formatCurrency(selectedRequest.amount)} from {selectedRequest?.staff_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Label className="form-label">Rejection Reason (Optional)</Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="form-input"
              placeholder="Provide a reason for rejection"
              data-testid="rejection-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} className="bg-red-600 hover:bg-red-700">
              Reject Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reverse Fund Dialog */}
      <AlertDialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={24} className="text-orange-500" />
              Reverse Approved Fund (Admin Override)
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will deduct {selectedRequest && formatCurrency(selectedRequest.amount)} from {selectedRequest?.staff_name}'s balance.
              <br /><br />
              <strong className="text-red-600">Warning: This action affects the staff's balance!</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Label className="form-label">Reason for Reversal <span className="text-red-500">*</span></Label>
            <Textarea
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              className="form-input"
              placeholder="e.g., Fund was credited in error"
              required
              data-testid="fund-reverse-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReverseFund} 
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-fund-reverse-btn"
            >
              Reverse Fund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Info Card */}
      <div className="card-flat">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">How Fund Requests Work</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Staff:</strong> Submit fund request when you receive money</li>
          <li>• <strong>Admin:</strong> Review and approve/reject requests</li>
          <li>• <strong>Approved:</strong> Amount is added to staff balance</li>
          <li>• <strong>Rejected:</strong> No balance change, staff notified</li>
          <li>• <strong>Reverse:</strong> Admin can reverse approved funds (deducts from balance)</li>
          <li>• <strong>Matching:</strong> Only approved (non-reversed) funds count in balance</li>
        </ul>
      </div>
    </div>
  );
}
